import { useSuspenseQuery } from '@apollo/client/react';

import { AppProtectionsDocument } from 'src/graphql/actions/apps/appProtections.query';
import { createRequiredContext } from 'src/context/createRequiredContext';

import type { CertificateBadge, ProductSlot, UnclaimedDetectedProduct } from 'src/widgets/AppCard/model';
import type { MappableApp } from './mapAppToCardData';

import {
  mapProtectionsToCertificateBadges,
  mapProtectionsToProductSlots,
  mapProtectionsToUnclaimedDetectedProducts,
} from './mapAppProtections';

const [useAppProtection, AppProtectionProvider] = createRequiredContext<{
  products: ProductSlot[];
  certificateBadges: CertificateBadge[];
  unclaimedDetectedProduct: UnclaimedDetectedProduct[];
}>();

type AppProtectionContextProviderProps = React.PropsWithChildren<{
  app: Pick<MappableApp, 'id' | 'owned' | 'name' | 'unclaimed'>;
}>;

const AppProtectionContextProvider = ({ app, children }: AppProtectionContextProviderProps) => {
  const { data } = useSuspenseQuery(AppProtectionsDocument, { variables: { appId: app.id } });

  const providerValue = {
    products: mapProtectionsToProductSlots(data.appProtections),
    certificateBadges: mapProtectionsToCertificateBadges(data.appProtections),
    unclaimedDetectedProduct: app.unclaimed ? mapProtectionsToUnclaimedDetectedProducts(data.appProtections, app) : [],
  };

  return <AppProtectionProvider value={providerValue}>{children}</AppProtectionProvider>;
};

export { useAppProtection, AppProtectionContextProvider };
